import { useMemo, useSyncExternalStore } from 'react'
import {
  getAssetStatus,
  getPhase1Progress,
  getPhase1Status,
  getPhase2Progress,
  getSceneLoadState,
  getSceneWantedAssets,
  subscribeSceneLoadTracker,
} from '../sceneLoadTracker'

export const loadingAttrs = (isLoading) =>
  isLoading ? { 'data-loading': 'true', 'aria-busy': 'true' } : {}

const isReadyStatus = (status) => status === 'done' || status === 'skipped'

const isLoadingStatus = (status) =>
  status == null || status === 'pending' || status === 'loading'

const readReadiness = (ids = []) => {
  const unique = [...new Set((ids || []).filter(Boolean))]
  if (!unique.length) return { ready: true, loading: false }

  let ready = true
  let loading = false
  for (const id of unique) {
    const status = getAssetStatus(id)
    if (isLoadingStatus(status)) {
      loading = true
      ready = false
      continue
    }
    if (!isReadyStatus(status)) ready = false
  }
  return { ready, loading }
}

export const useAssetReady = (ids = []) => {
  const idKey = Array.isArray(ids) ? ids.filter(Boolean).join('|') : ''
  const stableIds = useMemo(
    () => [...new Set((ids || []).filter(Boolean))],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idKey]
  )

  const statusKey = useSyncExternalStore(
    subscribeSceneLoadTracker,
    () =>
      stableIds
        .map((id) => `${id}:${getSceneLoadState().assets[id]?.status || 'null'}`)
        .join('|'),
    () =>
      stableIds
        .map((id) => `${id}:${getSceneLoadState().assets[id]?.status || 'null'}`)
        .join('|')
  )

  return useMemo(() => readReadiness(stableIds), [statusKey, stableIds])
}

export const useSceneWantedReady = () => {
  const wanted = useSceneWantedAssets()
  return useAssetReady(wanted)
}

export const useSceneWantedAssets = () =>
  useSyncExternalStore(
    subscribeSceneLoadTracker,
    getSceneWantedAssets,
    getSceneWantedAssets
  )

export const usePhase1Progress = () =>
  useSyncExternalStore(
    subscribeSceneLoadTracker,
    getPhase1Progress,
    () => 0
  )

export const usePhase1Status = () =>
  useSyncExternalStore(
    subscribeSceneLoadTracker,
    getPhase1Status,
    () => 'scene'
  )

export const usePhase2Progress = () => {
  const snapshotKey = useSyncExternalStore(
    subscribeSceneLoadTracker,
    () => {
      const progress = getPhase2Progress()
      return `${progress.current?.id || ''}:${Math.round((progress.fraction || 0) * 100)}`
    },
    () => ':0'
  )

  return useMemo(() => getPhase2Progress(), [snapshotKey])
}
